import { Role } from 'auth';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { Dispatch, SetStateAction, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import RegisterClassesSelectPaginate from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import { SelectHolder } from '@/modules/issuer-dashboard/components/transactions/SelectHolder.tsx';
import { useIssuerGetCertificatesQuery } from '@/modules/issuer-dashboard/queries/certificates.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Badge,
  BodyCell,
  EmptyListState,
  FilterItem,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { ArchiveBlackIcon, DownloadIcon, SendGreyIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';
import {
  GetListByIssuerV1SrCertificatesIssuerGetParams,
  ShareCertificateResponse,
  ShareCertificateStatus,
} from 'api';

export type Filters = Pick<
  GetListByIssuerV1SrCertificatesIssuerGetParams,
  'from_date' | 'to_date' | 'status' | 'shareholder_id' | 'register_class_id'
>;

const IssuerCertificates = () => {
  return (
    <PageLayout title="Certificates" contentClassName="px-0 py-2">
      <CertificateBody />
    </PageLayout>
  );
};

interface CertificateBodyProps {
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
}

export const CertificateBody = ({
  hideChooseRegisterClass,
  register_class_id,
}: CertificateBodyProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useSaveState<Filters>(
    {
      register_class_id,
    },
    `filters-certificate-${registerId}`,
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useIssuerGetCertificatesQuery(
    {
      page: 1,
      page_size: 30,
      status: filters.status,
      from_date: filters.from_date,
      to_date: filters.to_date,
      register_class_id: register_class_id ?? filters.register_class_id,
      shareholder_id: filters.shareholder_id,
    },
  );
  const certificates = (data?.pages || []).flatMap(({ data }) => data);

  const headItems = useMemo<TableHeadColumn[]>(() => {
    let items: TableHeadColumn[] = [
      {
        title: 'Investor',
      },
      { title: 'Class' },
      { title: 'Issue price' },
      { title: 'Shares/units' },
      { title: 'Total value' },
      { title: 'Date' },
      { title: 'Status' },
      { title: '', className: 'min-w-12' },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    return items;
  }, [hideChooseRegisterClass]);

  const handleOpenCommunicationModal = useCallback(
    (item: ShareCertificateResponse) => () => {
      const { entity, register_data, register_class } = item;
      openModal(IssuerAddAnnouncementModal, {
        certificates: [
          {
            label: (
              <div className="text-sm leading-[22px] font-[Inter] flex items-center gap-2">
                <div className="text-gray-900 whitespace-nowrap 2xl:max-w-[350px] sm:max-w-[200px] max-w-[130px] overflow-hidden text-ellipsis">
                  {entity?.entity_name || register_data?.name || '---'}
                </div>
              </div>
            ),
            value: item,
          },
        ],
        registerClass: register_class
          ? {
              value: register_class.id,
              label: register_class.class_name,
            }
          : undefined,
      });
    },
    [openModal],
  );

  const handleGoToHolderPage = useCallback(
    (entityId: number) => {
      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const items = useMemo<TableBodyColumn[]>(
    () =>
      certificates.map(certificate => {
        const {
          status,
          is_archived,
          archived_at,
          created_at,
          register_class,
          price_per_share,
          quantity_of_shares,
          file_storage_item_url,
        } = certificate;

        const entity = certificate.entity as unknown as EntityWithDocumentFullResponse;

        const entityName =
          ('business_name_trustee' in (entity?.smsf_entity_details || {}) &&
            !!entity?.smsf_entity_details?.business_name_trustee) ||
          ('business_name_trustee' in (entity?.trust_entity_details || {}) &&
            !!entity?.trust_entity_details?.business_name_trustee)
            ? `${
                entity?.trust_entity_details?.business_name_trustee ||
                entity?.smsf_entity_details?.business_name_trustee
              } <${entity?.entity_name}>`
            : `${entity?.entity_name}`;

        let cells: BodyCell[] = [
          {
            cell: (
              <span
                className="text-blue-700 font-medium cursor-pointer hover:underline"
                onClick={() => handleGoToHolderPage(entity.id)}
              >
                {entityName ?? '---'}
              </span>
            ),
          },
          { cell: register_class?.class_name || '-' },
          { cell: goodFormatUSDCurrency(price_per_share) },
          { cell: goodFormatNumber(quantity_of_shares) },
          { cell: goodFormatUSDCurrency(price_per_share * quantity_of_shares) },
          { cell: <>{dateToPrint(created_at, DateVariant.Table, true) || '-'}</> },
          {
            cell: (
              <Badge
                containerClass={classNames('text-white capitalize w-fit', {
                  'bg-green-600': status === ShareCertificateStatus.Current,
                  'bg-gray-400': status === ShareCertificateStatus.Outdated,
                })}
              >
                {status}
              </Badge>
            ),
          },
          {
            cell: (
              <div className="flex gap-4 select-none items-center sm:justify-end justify-center">
                {is_archived && (
                  <TooltipNew
                    text={`This certificate belongs to an archived transaction. It was archived at ${dateToPrint(
                      archived_at,
                      DateVariant.Information,
                      true,
                    )}`}
                  >
                    <img
                      src={ArchiveBlackIcon}
                      alt="Archive"
                      className="w-6 h-6 min-w-[24px] min-h-[24px]"
                    />
                  </TooltipNew>
                )}
                {status !== ShareCertificateStatus.Outdated && (
                  <button onClick={handleOpenCommunicationModal(certificate)}>
                    <img
                      src={SendGreyIcon}
                      alt="send"
                      className="cursor-pointer hover:opacity-80 w-6 h-6 min-w-[24px] min-h-[24px]"
                    />
                  </button>
                )}
                {file_storage_item_url && (
                  <div className="flex flex-wrap sm:flex-nowrap gap-2 items-center sm:justify-end justify-center">
                    <span
                      className="cursor-pointer hover:opacity-80"
                      onClick={e => e.stopPropagation()}
                    >
                      <a href={file_storage_item_url} target="_blank" rel="noopener noreferrer">
                        <img
                          alt="cerificate"
                          src={DownloadIcon}
                          className="w-6 h-6 min-w-[24px] min-h-[24px]"
                        />
                      </a>
                    </span>
                  </div>
                )}
              </div>
            ),
          },
        ];

        if (hideChooseRegisterClass) {
          cells = cells.filter((_, i) => i !== 1); //remove class
        }

        return {
          cells,
        };
      }),
    [certificates, handleGoToHolderPage, handleOpenCommunicationModal, hideChooseRegisterClass],
  );

  return (
    <>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
          <CertificateFiltersPanel
            setFilters={setFilters}
            filters={filters}
            isFetching={isFetching}
            hideChooseRegisterClass={hideChooseRegisterClass}
          />
        </div>
        {(!!items.length || isLoading) && (
          <Table
            classNameHead="border-t-0"
            withRounded={isMobile}
            withBorder={true}
            withBorderX={false}
            isLoading={isLoading}
            fetchNextPageInfinite={fetchNextPage}
            hasNextPageInfinite={hasNextPage}
            headItems={headItems}
            items={items}
            isSearchUsed={!isEmpty(filters)}
            blockIdInfinite="scrollable"
          />
        )}
        {!items.length && (
          <div className="flex items-center justify-between gap-4 w-full h-[60dvh] px-3 sm:px-6">
            <EmptyListState
              title="No certificates yet"
              description="It seems there are no certificates to display at the moment. Certificates are automatically generated after balance updates or transactions are completed."
              isLoading={isFetching}
              isSearchUsed={!isEmpty(filters)}
              itemsCount={items.length}
            />
          </div>
        )}
      </div>
    </>
  );
};

interface CertificateFiltersPanelProps {
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  isFetching: boolean;
  hideChooseRegisterClass?: boolean;
}

export const CertificateFiltersPanel = ({
  filters,
  setFilters,
  isFetching,
  hideChooseRegisterClass = false,
}: CertificateFiltersPanelProps) => {
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();

  const handleChangeStatus = useCallback(
    (status: ShareCertificateStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const dateValue =
    filters.from_date && filters.to_date
      ? ([dayjs(filters.from_date), dayjs(filters.to_date)] as [Dayjs, Dayjs])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              from_date: date[0].format('YYYY-MM-DD'),
              to_date: date[1].format('YYYY-MM-DD'),
            }
          : omit(filters, ['from_date', 'to_date']);
      });
    },
    [setFilters],
  );

  const handleChangeHolder = useCallback(
    (shareholder_id?: number) => {
      setFilters(filters => {
        return shareholder_id
          ? {
              ...filters,
              shareholder_id: +shareholder_id,
            }
          : omit(filters, ['shareholder_id']);
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (registerClassId: number | null) => {
      setFilters(filters => {
        return registerClassId
          ? {
              ...filters,
              register_class_id: +registerClassId,
            }
          : omit(filters, ['register_class_id']);
      });
    },
    [setFilters],
  );

  return (
    <div className="flex gap-2 items-center flex-wrap w-full">
      <FilterItem
        placeholder="Date"
        type="date"
        onChange={handleChangeDate}
        value={dateValue}
        className="rounded-lg"
      />

      {!hideChooseRegisterClass && (
        <RegisterClassesSelectPaginate
          registerId={+registerId}
          onChange={item => {
            handleChangeRegisterClass(item?.value.id || null);
          }}
          value={filters?.register_class_id}
          isMulti={false}
          placeholder="Class"
          label=""
          className="flex items-center border px-2 rounded-lg gap-2 w-full font-[Inter] text-sm leading-[22px] max-w-[350px] min-w-[200px]"
          classNameSelect="w-full"
          formFieldTitle=""
          showBorderBottom={false}
          withBorder={false}
          clearable={true}
        />
      )}

      <SelectHolder
        setShareholder={handleChangeHolder}
        shareholderId={filters.shareholder_id}
        registerId={registerId}
        placeholder="Holder"
        label=""
        isClearable={true}
        className="max-w-[250px] min-w-[200px]"
      />

      <FilterItem
        placeholder="Status"
        type="select"
        value={filters.status}
        options={getSelectOptionByEnum<ShareCertificateStatus>(ShareCertificateStatus)}
        onChange={handleChangeStatus}
        className="rounded-lg"
      />

      {isFetching && <Spinner size="md" variant="primary" />}
    </div>
  );
};

export default withPrivateAccess(IssuerCertificates, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
